import { useState, useEffect } from "react";
import { getMyOrders } from "../../http/api";
import { isOrderForRegularBlotter } from "./useWebSocketUpdates";

// Helper function to ensure we never have more than the specified number of items
const limitToMaxItems = (array, maxItems) => {
  return array.length > maxItems ? array.slice(0, maxItems) : array;
};

export const useOrders = (token, page = 0, size = 20, columnToSort = "updatedAt", sortingDirection = "desc", visitedTabs = new Set(), setVisitedTabs = () => {}, tabId = "1-0") => {
  const [orderData, setOrderData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Skip API call if token is null (meaning this tab is not active)
    if (!token) return;

    // Check if we've already visited this tab and page combination
    // If we have, don't make the API call again
    const tabPageKey = `${tabId}-${page}-${size}`;
    if (visitedTabs.has(tabPageKey)) {
      return;
    }

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await getMyOrders(token, page, size, columnToSort, sortingDirection);
        // Filter out orders that belong in the Staging Blotter (keep only live orders)
        const filteredOrders = (response.content || []).filter(isOrderForRegularBlotter);
        // Pagination counts parents. Keep the central state flat so order actions and
        // quote updates continue to address every child by its own orderId.
        setOrderData(limitToMaxItems(filteredOrders, size).flatMap((order) =>
          Array.isArray(order.listOrders) && order.listOrders.length
            ? order.listOrders.filter(isOrderForRegularBlotter) : [order]
        ));
        setTotalElements(response.totalElements || 0);
        
        // Mark this tab-page combination as visited by replacing any existing entries for this tab
        setVisitedTabs(prev => {
          const newSet = new Set(prev);
          // Remove any previous entries for this tab (regardless of page)
          Array.from(prev).forEach(key => {
            if (key.startsWith(`${tabId}-`)) {
              newSet.delete(key);
            }
          });
          // Add the current tab-page combination
          newSet.add(tabPageKey);
          return newSet;
        });
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [token, page, size, columnToSort, sortingDirection, tabId, visitedTabs, setVisitedTabs]);

  return { orderData, setOrderData, totalElements, setTotalElements, loading };
};
